/**
 * Utility to interact with Open Library API
 */

interface OpenLibraryDoc {
    key: string
    cover_edition_key?: string
    edition_key?: string[]
    publisher?: string[]
    number_of_pages_median?: number
    author_name?: string[]
    subject?: string[]
}

interface OpenLibraryResponse {
    docs: OpenLibraryDoc[]
}

interface WorkData {
    description?: string | { value: string }
    subjects?: string[]
}

interface EditionData {
    description?: string | { value: string }
    publishers?: string[]
    number_of_pages?: number
    subjects?: string[]
}

export interface BookInfo {
    description: string
    authors: string[]
    categories: string[]
    page_count: number
    publisher: string
}

/**
 * Extraction helper for description
 */
const extractDescription = (data: WorkData | EditionData | null): string => {
    if (!data?.description) return ''
    if (typeof data.description === 'string') return data.description
    if (typeof data.description === 'object' && data.description.value) return data.description.value
    return ''
}

export const searchOpenLibraryInfo = async (title: string, author?: string | string[]): Promise<BookInfo | null> => {
    if (!title) return null

    try {
        // 1. Search for the book
        let query = `title=${encodeURIComponent(title)}`
        const searchAuthor = Array.isArray(author) ? author[0] : author
        if (searchAuthor) {
            query += `&author=${encodeURIComponent(searchAuthor)}`
        }

        const searchRes = await fetch(`https://openlibrary.org/search.json?${query}&limit=1`)
        if (!searchRes.ok) throw new Error('Open Library search failed')

        const searchData = (await searchRes.json()) as OpenLibraryResponse
        if (!searchData.docs || searchData.docs.length === 0) return null

        const doc = searchData.docs[0]
        const workKey = doc.key // e.g. "/works/OL27482W"
        const editionKey = doc.cover_edition_key ?? doc.edition_key?.[0]

        // 2. Fetch Work and Edition details in parallel
        const [workData, editionData] = await Promise.all([
            workKey
                ? fetch(`https://openlibrary.org${workKey}.json`).then((res) => (res.ok ? res.json() : null))
                : Promise.resolve(null),
            editionKey
                ? fetch(`https://openlibrary.org/books/${editionKey}.json`).then((res) => (res.ok ? res.json() : null))
                : Promise.resolve(null),
        ])

        if (!workData && !editionData) return null

        const description = extractDescription(workData as WorkData) || extractDescription(editionData as EditionData) || ''

        // Publishers
        const publishers = (editionData as EditionData)?.publishers || doc.publisher || []
        const publisher = publishers.length > 0 ? publishers[0] : ''

        // Pages
        const page_count = (editionData as EditionData)?.number_of_pages || doc.number_of_pages_median || 0

        // Authors - return as array
        const authors = doc.author_name || []

        // Subjects / Categories - return first 5 as array
        const subjects = (workData as WorkData)?.subjects || (editionData as EditionData)?.subjects || doc.subject || []
        const categories = subjects.slice(0, 5)

        return {
            description,
            authors,
            categories,
            page_count,
            publisher,
        }
    } catch (error) {
        console.warn('Error fetching from Open Library:', error)
        return null
    }
}
